import cx from 'classnames';
import ParentalConsentWarning from '../components/ParentalConsentWarning/ParentalConsentWarning';
import { useIsFederatedSignInAvailable } from '../useIsFederatedSignInAvailable';
import googleClassroomLogo from '../images/32x32_yellow_stroke_icon@1x.png';
import cleverClassroomLogo from '../images/CleverClassroom-Icon.png';
import classLinkClassroomLogo from '../images/ClassLinkClassroom-Icon.png';
import styles from '../SCSS/ClassroomCard.module.scss';

type Props = {
  onGoogleImport: () => void;
  onCleverImport: () => void;
  onClassLinkImport: () => void;
};

/**
 * The "Class Import" section of the classroom settings page.
 *
 * Renders nothing when the account's backend region has no federated sign-in:
 * every import here signs the teacher in with Google, Clever or ClassLink
 * first, which would conflict with the FRA Cognito pools, so the whole section
 * is a dead end for those accounts (PE-1785). The heading goes with the
 * buttons - a lone "Class Import" title with nothing under it reads as
 * breakage - and so does the parental-consent notice, which belongs to this
 * flow; the student-creation paths carry their own.
 */
export default function ClassImportSection({ onGoogleImport, onCleverImport, onClassLinkImport }: Props) {
  const isFederatedSignInAvailable = useIsFederatedSignInAvailable();

  if (!isFederatedSignInAvailable) {
    return null;
  }

  return (
    <div className={styles.buttonsSection}>
      <h3 className={styles.classImportTitle}>Class Import</h3>
      <div className={styles.buttonsContainer}>
        <button
          type='button'
          onClick={onGoogleImport}
          className={cx(styles.importClassroomButton, styles.importClassroomButtonGoogle)}
        >
          <img alt='' src={googleClassroomLogo} />
          <span>Import with Google Classroom</span>
        </button>
        <button
          type='button'
          onClick={onCleverImport}
          className={cx(styles.importClassroomButton, styles.importClassroomButtonBlue)}
        >
          <img alt='' src={cleverClassroomLogo} />
          <span>Import with Clever</span>
        </button>
        <button
          type='button'
          onClick={onClassLinkImport}
          className={cx(styles.importClassroomButton, styles.importClassroomButtonClassLink)}
        >
          <img alt='' src={classLinkClassroomLogo} />
          <span>Import with ClassLink</span>
        </button>
      </div>
      <div className={styles.warning}>
        <ParentalConsentWarning moreStyles={styles.parentalConsentWarning} />
      </div>
    </div>
  );
}
